import React from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import { NETWORK_DESCRIPTIONS } from "QuotePage/NetworkDescriptions";
import { NETWORK_SIZE_CONSTANTS } from "QuotePage/NetworkSizeConstants";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { Popover } from "Cobalt/Popover";
import { Table, TR, TD } from "Cobalt/TableV2";
import BodyText from "Common/BodyText";
import { Card, CardHeader, CardSection } from "Common/Card";
import Icon from "Common/Icon";
import InfoBox from "Common/InfoBox/InfoBox";
import SIOverlay from "Common/SIOverlay";

const DoctorNetwork = ({ plan }) => {
  const tableConfiguration = [
    { label: "Plan Network", key: "network" },
    { label: "Network Type", key: "plan_type" },
    { label: "Network Size", key: "network_size" },
    { label: "Doctor Search", key: "" },
    { label: "Covers Out-of-State Employees?", key: "out_of_state" },
    { label: "Requires Referral to See Specialist?", key: "referral_required" },
    { label: "International Coverage", key: "" },
  ];

  const translatePlanValue = (config) => {
    let planValue = plan[config.key];
    if (config.label === "Covers Out-of-State Employees?") {
      planValue = plan.out_of_state ? "Yes" : "No";
    } else if (config.label === "Requires Referral to See Specialist?") {
      planValue = plan.referral_required ? "Yes" : "No";
    } else if (config.label === "Doctor Search") {
      planValue = (
        <a
          href="https://support.simplyinsured.com/s/article/In-Network-Doctor-Search-Tools-by-Insurance-Carrier"
          target="_blank"
          onClick={() =>
            AnalyticsTracking.trackEvent("link_clicked", {
              location: "doctor_network_doctor_search",
              carrier: plan.carrier,
              state: plan.state,
              network: plan.network,
            })
          }
          rel="noreferrer"
        >
          {`${plan.network} Doctor Search`} <Icon icon="external-link-alt" flavor="solid" />
        </a>
      );
    } else if (config.label === "International Coverage") {
      planValue = "Emergency Only";
    }
    return planValue;
  };

  const rows = tableConfiguration.map((config, i) => {
    const planValue = translatePlanValue(config);

    if (planValue) {
      if (config.label === "Network Type") {
        const planKeyPopover = (
          <Popover showBackdrop={false} className="plan-term-tooltip" id={planValue}>
            {NETWORK_DESCRIPTIONS[planValue]}
          </Popover>
        );

        const target = <span className="SIOverlayTrigger__base">{planValue}</span>;

        return (
          <TR className="content-row" key={i} highlightFirstCell={false}>
            <TD style={{ width: "50%" }}>{config.label}</TD>
            <TD style={{ width: "50%" }}>
              <SIOverlay placement="right" overlayContent={planKeyPopover} overlayTarget={target} trackType="dashed-plan-term" trackId={planValue} />
            </TD>
          </TR>
        );
      }
      if (config.label === "Plan Network") {
        return (
          <TR className="content-row" key={i} highlightFirstCell={false}>
            <TD style={{ width: "50%" }}>{config.label}</TD>
            <TD style={{ width: "50%" }}>{planValue}</TD>
          </TR>
        );
      }
      if (config.label === "Network Size") {
        const { tooltip } = NETWORK_SIZE_CONSTANTS[plan[config.key]];
        const stars = NETWORK_SIZE_CONSTANTS[plan[config.key]].label;
        const planKeyPopover = (
          <Popover showBackdrop={false} className="plan-term-tooltip" id={planValue}>
            {tooltip}
          </Popover>
        );

        const target = <span className="SIOverlayTrigger__base">{stars}</span>;
        return (
          <TR className="content-row" key={i} highlightFirstCell={false}>
            <TD style={{ width: "50%" }}>{config.label}</TD>
            <TD style={{ width: "50%" }}>
              <SIOverlay placement="right" overlayContent={planKeyPopover} overlayTarget={target} trackType="dashed-plan-term" trackId={stars} />
            </TD>
          </TR>
        );
      }
      if (config.label === "International Coverage") {
        const tooltip = "This is the case for all plans.";
        const planKeyPopover = (
          <Popover showBackdrop={false} className="plan-term-tooltip" id={planValue}>
            {tooltip}
          </Popover>
        );

        const target = <span className="SIOverlayTrigger__base">{planValue}</span>;

        return (
          <TR className="content-row" key={i} highlightFirstCell={false}>
            <TD style={{ width: "50%" }}>{config.label}</TD>
            <TD style={{ width: "50%" }}>
              <SIOverlay placement="right" overlayContent={planKeyPopover} overlayTarget={target} trackType="dashed-plan-term" trackId={planValue} />
            </TD>
          </TR>
        );
      }

      return (
        <TR className="content-row" key={i} highlightFirstCell={false}>
          <TD style={{ width: "50%" }}>{config.label}</TD>
          <TD style={{ width: "50%" }}>{planValue}</TD>
        </TR>
      );
    }
  });

  return (
    <Card theme="flat" className={classnames("section-holder")} id="doctorNetwork" p={0}>
      <CardHeader>Doctor Network</CardHeader>
      <CardSection>
        <BodyText>
          <Table id={`doctor-network-${plan.id}`}>
            <tbody>{rows}</tbody>
          </Table>
        </BodyText>
      </CardSection>
      <CardSection>
        <InfoBox
          infoText="If you're forced to go to an out-of-network provider for an urgent care
                    or emergency issue, it will be covered, including while you're traveling."
        />
      </CardSection>
    </Card>
  );
};

DoctorNetwork.propTypes = {
  plan: PropTypes.object,
};

export default DoctorNetwork;
